/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { shallowEqual, useDispatch, useSelector, useStore } from 'react-redux';
import { useAuthenticationContext } from '@opengeoweb/authentication';
import { AlertBanner, useConfirmationDialog } from '@opengeoweb/shared';
import {
  genericSelectors,
  layerActions,
  syncGroupsSelector,
} from '@opengeoweb/store';
import { WarningsTimeline } from './WarningsTimeline';
import {
  compareWarningsByStatus,
  getEffectiveStatus,
} from './warningsTimelineUtils';
import { useWarnings } from '../../api/api-utils';
import { warningsFiltersActions } from '../../store';
import type { WarningModuleStore } from '../../store/types';
import {
  getExpandedSectionsByPanelId,
  getHiddenWarningIdsByPanelId,
  getHoveredWarningIdsByPanelId,
  getWarningsFilterById,
} from '../../store/warningsFilters/selectors';
import { warningsFormActions } from '../../store/warningsForm/reducer';
import {
  getSelectedPublicIsFormDirty,
  getSelectedWarningId,
  getWarningType,
} from '../../store/warningsForm/selectors';
import type { Warning } from '../../store/warningsForm/types';
import { MAX_WARNINGS_DISPLAY } from '../../utils/constants';
import { useWarningsTranslation } from '../../utils/i18n';
import { createWarningFilterPredicate } from '../FilterList/filter-hooks';
import type { FilterState } from '../FilterList/filter-hooks';
import { TimeFilterMode } from '../FilterList/filter-utils';
import { WARNING_HOVER_DISPATCH_DELAY } from '../PublicWarningList/useWarningListFeatureSync';
import type { WarningListItemActions } from '../PublicWarningList/WarningListItemMenu';
import { warningFormConfirmationOptions } from '../PublicWarningsFormDialog/PublicWarningsFormDialogConnect';
import { useWarningEventActions } from '../WarningEvents/WarningEventDialog';

export interface WarningsTimelineConnectProps {
  panelId: string;
}

interface WorkspaceViewsById {
  [viewId: string]: {
    componentType?: string;
  };
}

const getLinkedWarningListPanelId = (
  viewsById: WorkspaceViewsById,
  linksByPanelId: Record<string, string[]>,
  panelId: string,
): string | undefined => {
  // Timeline is a read-only link target for the list (for now)
  return Object.keys(linksByPanelId).find((candidatePanelId) => {
    if (candidatePanelId === panelId) {
      return false;
    }
    const candidateType = viewsById[candidatePanelId]?.componentType;
    if (candidateType !== 'WarningList') {
      return false;
    }
    const candidateTargets = linksByPanelId[candidatePanelId] || [];
    return candidateTargets.includes(panelId);
  });
};

export const WarningsTimelineConnect: React.FC<
  WarningsTimelineConnectProps
> = ({ panelId }) => {
  const { t } = useWarningsTranslation();
  const { isLoggedIn } = useAuthenticationContext();
  const dispatch = useDispatch();
  const confirmDialog = useConfirmationDialog();

  const workspaceViewsById = useSelector((state: WarningModuleStore) =>
    syncGroupsSelector.getWorkspaceViewsById(state),
  ) as WorkspaceViewsById;
  const linksByPanelId = useSelector((state: WarningModuleStore) =>
    genericSelectors.selectFeatureLinksForAllPanels(state),
  );
  const sourcePanelId = React.useMemo(
    () =>
      getLinkedWarningListPanelId(workspaceViewsById, linksByPanelId, panelId),
    [workspaceViewsById, linksByPanelId, panelId],
  );

  const savedFilterState = useSelector((state: WarningModuleStore) =>
    getWarningsFilterById(state, sourcePanelId),
  );

  const expandedSections = useSelector((state: WarningModuleStore) =>
    getExpandedSectionsByPanelId(state, sourcePanelId),
  );

  const hiddenWarningIds = useSelector((state: WarningModuleStore) =>
    getHiddenWarningIdsByPanelId(state, sourcePanelId),
  );

  const store = useStore<WarningModuleStore>();
  const ownedFeatureLayerIds = useSelector(
    (state: WarningModuleStore) =>
      sourcePanelId
        ? genericSelectors.selectFeatureSyncLayerIdsForPanel(
            state,
            sourcePanelId,
          )
        : [],
    shallowEqual,
  );
  const linkedTargetIds = useSelector(
    (state: WarningModuleStore) =>
      sourcePanelId
        ? genericSelectors.getLinkedTargetIds(state, sourcePanelId)
        : [],
    shallowEqual,
  );
  const hoveredLinkedFeatureIds = useSelector(
    (state: WarningModuleStore) =>
      sourcePanelId
        ? genericSelectors.selectHoveredFeatureIdsForPanel(state, sourcePanelId)
        : [],
    shallowEqual,
  );
  const hoveredLocalWarningIds = useSelector((state: WarningModuleStore) =>
    getHoveredWarningIdsByPanelId(state, sourcePanelId),
  );
  const [localHoveredWarningId, setLocalHoveredWarningId] = React.useState<
    string | undefined
  >(undefined);
  const [isUsingLocalHoverOverride, setIsUsingLocalHoverOverride] =
    React.useState(false);
  const hoverDispatchTimeoutRef = React.useRef<number | undefined>(undefined);
  const hoveredWarningId = isUsingLocalHoverOverride
    ? localHoveredWarningId
    : (hoveredLinkedFeatureIds[0] ?? hoveredLocalWarningIds[0]);

  const { combinedWarnings } = useWarnings(
    !isLoggedIn || !sourcePanelId,
    savedFilterState.time,
  );
  const isFormDirty = useSelector(getSelectedPublicIsFormDirty);
  const selectedWarningId = useSelector(getSelectedWarningId);
  const warningType = useSelector(getWarningType);
  const { openWarningsEventDialog } = useWarningEventActions(
    sourcePanelId || '',
    savedFilterState.time,
  );

  const byFilterState = React.useMemo(
    () =>
      createWarningFilterPredicate(
        combinedWarnings,
        savedFilterState as FilterState,
      ),
    [combinedWarnings, savedFilterState],
  );

  const filteredWarnings = React.useMemo(
    () => combinedWarnings.filter(byFilterState),
    [byFilterState, combinedWarnings],
  );

  const displayedWarnings = React.useMemo(() => {
    const isOverLimit = filteredWarnings.length > MAX_WARNINGS_DISPLAY;
    const hasCustomTimeFilter =
      savedFilterState.time.mode === TimeFilterMode.custom;
    return hasCustomTimeFilter && isOverLimit ? [] : filteredWarnings;
  }, [filteredWarnings, savedFilterState.time.mode]);

  const warnings = React.useMemo(() => {
    if (!sourcePanelId) {
      return [];
    }
    return displayedWarnings
      .filter((warning) => expandedSections[getEffectiveStatus(warning)])
      .filter((warning) => !hiddenWarningIds.includes(warning.id!))
      .sort(compareWarningsByStatus);
  }, [displayedWarnings, expandedSections, hiddenWarningIds, sourcePanelId]);

  const handleHoverWarning = React.useCallback(
    (warningId?: string): void => {
      if (!sourcePanelId) {
        return;
      }

      setLocalHoveredWarningId(warningId);
      setIsUsingLocalHoverOverride(true);

      if (hoverDispatchTimeoutRef.current) {
        window.clearTimeout(hoverDispatchTimeoutRef.current);
      }

      hoverDispatchTimeoutRef.current = window.setTimeout(() => {
        if (!ownedFeatureLayerIds.length && linkedTargetIds.length) {
          dispatch(
            warningsFiltersActions.setHoveredWarningIdsForPanel({
              panelId: sourcePanelId,
              warningIds: warningId ? [warningId] : [],
            }),
          );
        }
        ownedFeatureLayerIds.forEach((layerId) => {
          dispatch(
            layerActions.setHoveredFeatures({
              layerId,
              hoveredFeatureIds: warningId ? [warningId] : [],
            }),
          );
        });
        setIsUsingLocalHoverOverride(false);
      }, WARNING_HOVER_DISPATCH_DELAY);
    },
    [dispatch, linkedTargetIds.length, ownedFeatureLayerIds, sourcePanelId],
  );

  const openPublicWarningDialog = React.useCallback(
    async (warning: Warning): Promise<void> => {
      if (!sourcePanelId) {
        return;
      }

      if (isFormDirty) {
        const mayProceed = await confirmDialog(
          warningFormConfirmationOptions(t),
        )
          .then(() => true)
          .catch(() => false);

        if (!mayProceed) {
          return;
        }
      }

      dispatch(
        warningsFormActions.openPublicWarningFormDialog({
          warning,
          formState: 'readonly',
          warningType: warning.type || warningType,
          panelId: sourcePanelId,
        }),
      );
    },
    [confirmDialog, dispatch, isFormDirty, sourcePanelId, t, warningType],
  );

  const warningListItemActions = React.useMemo<WarningListItemActions>(
    () => ({
      onEditWarning: (warning): void => {
        if (!sourcePanelId) {
          return;
        }
        dispatch(
          warningsFiltersActions.requestWarningListAction({
            panelId: sourcePanelId,
            request: { type: 'edit', warning },
          }),
        );
      },
      onDeleteWarning: (warning): void => {
        if (!sourcePanelId) {
          return;
        }
        dispatch(
          warningsFiltersActions.requestWarningListAction({
            panelId: sourcePanelId,
            request: { type: 'delete', warning },
          }),
        );
      },
      onDeleteReviewWarning: (warning): void => {
        if (!sourcePanelId) {
          return;
        }
        dispatch(
          warningsFiltersActions.requestWarningListAction({
            panelId: sourcePanelId,
            request: { type: 'deleteReview', warning },
          }),
        );
      },
      onExpireWarning: (warning): void => {
        if (!sourcePanelId) {
          return;
        }
        dispatch(
          warningsFiltersActions.requestWarningListAction({
            panelId: sourcePanelId,
            request: { type: 'expire', warning },
          }),
        );
      },
      onWithdrawWarning: (warning): void => {
        if (!sourcePanelId) {
          return;
        }
        dispatch(
          warningsFiltersActions.requestWarningListAction({
            panelId: sourcePanelId,
            request: { type: 'withdraw', warning },
          }),
        );
      },
      onActionAviationWarning: (warning, actionType): void => {
        if (!sourcePanelId) {
          return;
        }
        dispatch(
          warningsFiltersActions.requestWarningListAction({
            panelId: sourcePanelId,
            request: { type: 'aviation', warning, actionType },
          }),
        );
      },
      onActionMaritimeWarning: (warning, actionType): void => {
        if (!sourcePanelId) {
          return;
        }
        dispatch(
          warningsFiltersActions.requestWarningListAction({
            panelId: sourcePanelId,
            request: { type: 'maritime', warning, actionType },
          }),
        );
      },
    }),
    [dispatch, sourcePanelId],
  );

  const selectWarning = React.useCallback(
    (warning: Warning): void => {
      if (warning.type === 'warningsEvent') {
        void openWarningsEventDialog('readonly', warning);
        return;
      }

      void openPublicWarningDialog(warning);
    },
    [openPublicWarningDialog, openWarningsEventDialog],
  );

  React.useEffect(() => {
    return (): void => {
      if (!sourcePanelId) {
        return;
      }

      if (hoverDispatchTimeoutRef.current) {
        window.clearTimeout(hoverDispatchTimeoutRef.current);
      }

      const ownedLayerIds = genericSelectors.selectFeatureSyncLayerIdsForPanel(
        store.getState(),
        sourcePanelId,
      );
      const currentLinkedTargetIds = genericSelectors.getLinkedTargetIds(
        store.getState(),
        sourcePanelId,
      );

      if (!ownedLayerIds.length && currentLinkedTargetIds.length) {
        dispatch(
          warningsFiltersActions.setHoveredWarningIdsForPanel({
            panelId: sourcePanelId,
            warningIds: [],
          }),
        );
      }

      ownedLayerIds.forEach((layerId) => {
        dispatch(
          layerActions.setHoveredFeatures({
            layerId,
            hoveredFeatureIds: [],
          }),
        );
      });
    };
  }, [dispatch, sourcePanelId, store]);

  if (!isLoggedIn) {
    return (
      <AlertBanner
        dataTestId="warnings-timeline-auth-error"
        severity="error"
        title={t('error-auth-login')}
      />
    );
  }

  return (
    <WarningsTimeline
      hoveredWarningId={hoveredWarningId}
      onHoverWarning={handleHoverWarning}
      onSelectWarning={selectWarning}
      selectedWarningId={selectedWarningId}
      warningListItemActions={warningListItemActions}
      warnings={warnings}
    />
  );
};
